import React, { useEffect, useMemo, useState } from "react";
import { FaEye, FaPlus, FaUserCog } from "react-icons/fa";
import styles from "../master/academicConfiguration/AcademicCreate.module.css";
import AxiosInstance, { TokenService } from "../../../services/Api";
import { CustomButton, CustomDropdown } from "../../../components";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";

const CreatetimetbListView = () => {
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const { translations } = useSelector((state) => state.language);
  const navigate = useNavigate();

  /* ===========================
     STATES
  =========================== */
  const [classValue, setClassValue] = useState("");
  const [sectionValue, setSectionValue] = useState("");
  const [shiftValue, setShiftValue] = useState("");
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const [data, setData] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [errors, setErrors] = useState({});

  // API call to fetch class list
  const fetchClassList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/school/classList`, {
        id: school_id,
        academic_year: academic_year,
      });

      if (response?.data?.success) {
        const apiData = response.data.data || [];
        const transformedData = apiData
          .filter((item) => item.domain_id !== "CLASS")
          .map((item) => ({
            id: item.id,
            class_id: item.class_id,
            class: item.domain_id,
            label: item.domain_id,
            value: item.class_id,
            maleCount: item.no_of_boys || 0,
            femaleCount: item.no_of_girls || 0,
            totalStudents: (item.no_of_boys || 0) + (item.no_of_girls || 0),
            noOfSection: item.no_of_section || 0,
          }));
        setData(transformedData);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching class list:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        `/school/timeTable/sectionDropdown `,
        {
          id: [classValue.class_id],
          school_id: school_id,
        },
      );
      if (response?.data?.status) {
        const apiData = response.data.data || [];
        const transformedData = apiData.map((item) => ({
          id: item.id,
          class_id: item.class_id,
          section: item.section_id,
          label: item.section_id,
          value: item.section_id,
        }));
        setSectionList(transformedData);
      } else {
        setSectionList([]);
      }
    } catch (error) {
      console.error("Error fetching section list:", error);
      setSectionList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchShiftList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/school/shiftList/${school_id}`,
      );
      if (response?.data?.status) {
        const apiData = response.data.data || [];
        const transformedData = apiData.map((item) => ({
          value: item.id,
          label: item.shift_type,
        }));
        setShiftList(transformedData);
      } else {
        setShiftList([]);
      }
    } catch (error) {
      console.error("Error fetching shift list:", error);
      setShiftList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (classValue) {
      fetchSectionList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classValue]);

  useEffect(() => {
    if (school_id) {
      fetchClassList();
      fetchShiftList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id]);

  /* ===========================
     FETCH TABLE DATA
  =========================== */
  useEffect(() => {
    if (classValue && sectionValue && shiftValue) {
      fetchTimeTableSchool();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classValue, sectionValue, shiftValue]);

  const fetchTimeTableSchool = async () => {
    setLoading(true);
    const weekendId = getTodayWeekendId();
    try {
      const response = await AxiosInstance.post("/timeTable/timeTableSchool", {
        school_id: school_id,
        class_id: classValue.value,
        section: sectionValue.value,
        weekend_id: String(weekendId),
        dayid: "",
        shift_id: shiftValue.value,
        academic_year: academic_year_id,
      });
      if (response?.data) {
        setTableData(response.data.data || []);
      } else {
        setTableData([]);
      }
    } catch (error) {
      console.error("Error fetching day schedule", error);
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!classValue) newErrors.classValue = translations.errors.classRequired;
    if (!sectionValue)
      newErrors.sectionValue = translations.errors.sectionRequired;
    if (!shiftValue) newErrors.shiftValue = translations.errors.shiftRequired;
    return newErrors;
  };

  /* ===========================
     TABLE COLUMNS (DYNAMIC)
  =========================== */
  const handleView = (rowData) => {
    navigate("/user/ViewTimeTable", {
      state: {
        DAY: rowData.day_id,
        CLASSID: classValue,
        SECTION: sectionValue,
        SHIFTID: shiftValue,
      },
    });
  };

  const DAY_MAP = {
    1: "MONDAY",
    2: "TUESDAY",
    3: "WEDNESDAY",
    4: "THURSDAY",
    5: "FRIDAY",
    6: "SATURDAY",
    7: "SUNDAY",
  };

  const { formattedData, dynamicPeriods } = useMemo(() => {
    const grouped = {};
    const periodSet = new Set();

    tableData.forEach((item) => {
      const { dayid, period, subject_name, staff_name } = item;
      periodSet.add(period);

      if (!grouped[dayid]) {
        grouped[dayid] = {
          day: DAY_MAP[dayid] || `Day ${dayid}`,
          day_id: dayid,
          schedule: `Day ${dayid}`,
        };
      }

      // ✅ Store as object instead of joined string
      grouped[dayid][`period_${period}`] = {
        subject: subject_name || "-",
        staff: staff_name || "-",
      };
    });

    return {
      formattedData: Object.values(grouped),
      dynamicPeriods: Array.from(periodSet).sort((a, b) => a - b),
    };
  }, [tableData]);

  const columns = useMemo(() => {
    if (!dynamicPeriods.length) return [];

    // ✅ Custom Cell renderer for period — subject on top, staff below
    const periodColumns = dynamicPeriods.map((period) => ({
      accessorKey: `period_${period}`,
      header: `Period ${period}`,
      enableSorting: false,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (!value || typeof value !== "object") return "-";
        return (
          <div style={{ lineHeight: "1.4" }}>
            <div>{value.subject}</div>
            <div>{value.staff}</div>
          </div>
        );
      },
    }));

    return [
      {
        accessorKey: "day",
        header: "Day",
        enableSorting: false,
      },
      {
        accessorKey: "schedule",
        header: "Schedule",
        enableSorting: false,
      },
      ...periodColumns,
      {
        id: "actions",
        header: translations.labels?.actions || "Actions",
        enableSorting: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={translations.labels?.viewStudent || "View Student"}
          >
            <FaEye />
          </button>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicPeriods, translations]);

  const handleDropdownChange = (field, value, setter) => {
    setter(value);
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));

    if (field === "classValue") {
      setSectionValue("");
      setSectionList([]);
    }
  };

  const handleAdd = () => {
    navigate("/user/createtimetbList");
  };

  const getTodayWeekendId = () => {
    const today = new Date().getDay();
    return today === 0 ? 7 : today;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaUserCog />
        </div>
        <h1 className={styles.headerTitle}>
          {translations?.labels?.timeTableListTitle || "Time Table List"}
        </h1>
      </div>

      {/* FILTER CLASS */}
      <div className="row mb-4">
        <div className="col-lg-4 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={translations?.labels?.classLabel}
            value={classValue}
            options={data}
            onChange={(value) =>
              handleDropdownChange("classValue", value, setClassValue)
            }
            placeholder={translations?.labels?.classPlaceholder}
            required
            error={errors.classValue}
          />
        </div>

        {/* FILTER SECTION */}
        <div className="col-lg-4 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={translations?.labels?.sectionLabel}
            value={sectionValue}
            options={sectionList}
            onChange={(value) =>
              handleDropdownChange("sectionValue", value, setSectionValue)
            }
            placeholder={translations?.labels?.sectionPlaceholder}
            required
            error={errors.sectionValue}
          />
        </div>

        {/* FILTER SHIFT */}
        <div className="col-lg-4 col-md-6 col-sm-12 px-2">
          <CustomDropdown
            label={translations?.labels?.shiftLabel}
            value={shiftValue}
            options={shiftList}
            onChange={(value) =>
              handleDropdownChange("shiftValue", value, setShiftValue)
            }
            placeholder={translations?.labels?.shiftPlaceholder}
            required
            error={errors.shiftValue}
          />
        </div>
      </div>

      <div className="col-md-12 p-3 d-flex justify-content-end align-items-center">
        <div className="row ">
          <CustomButton
            variant="contained"
            onClick={handleAdd}
            className={styles.saveButton}
            fullWidth={false}
          >
            <FaPlus style={{ marginRight: "8px" }} />
            {translations.labels.add}
          </CustomButton>
        </div>
      </div>

      {/* ===========================
          TABLE (LOAD AFTER SELECT)
      =========================== */}
      {classValue && sectionValue && shiftValue && (
        <CustomMRTTable
          columns={columns}
          data={formattedData}
          loading={loading}
          pagination={pagination}
          onPaginationChange={setPagination}
          rowCount={formattedData.length}
          enablePagination={false}
          enableSearch={false}
          enableFiltering={false}
        />
      )}
    </div>
  );
};

export default CreatetimetbListView;
